import {ManagedScene2} from "../ManagedScene2";
import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {FrmVar} from "../../drawfrm/FrmVar";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {FrameGridView} from "../frameGrid/FrameGridScene";
import {TopDownView} from "../frameProfile/TopDownScene";
import {NavButtons} from "../../shared-2d/NavButtons";

export class FrameAllScene extends ManagedScene2 {
    private grid: FrameGridView | null = null;
    private topDown: TopDownView | null = null;
    // the top-down drawables live under this container so the top-down half can be
    // rebuilt in place (on grid edits) without touching the grid view — rebuilding the
    // grid would break an in-progress mullion drag.
    private topDownRoot: DrawFrmThing | null = null;

    private structureSub: (() => void) | null = null;
    private valueSub: {cancel(): void} | null = null;
    private rebuildScheduled = false;
    private building = false;

    setup(app: DrawFrmApp) {
        super.setup(app);

        this.topDownRoot = new DrawFrmThing();
        this.add(this.topDownRoot);
        this.grid = this.track(new FrameGridView(this.elev, input => this.add(input)));

        this.building = true;
        this.buildTopDown();
        this.building = false;

        // the top-down mirrors the grid, so rebuild it on any structural or value edit
        // to stay in sync. The grid maintains itself in place (and owns any active
        // drag), so it's left alone.
        this.structureSub = this.elev.frameSpec.frameBlocksChanged.subscribe(() => this.scheduleTopDownRebuild());
        this.valueSub = FrmVar.anyUserSet.subscribe(() => this.scheduleTopDownRebuild());
    }

    private buildTopDown() {
        if(!this.topDownRoot || !this.grid) return;

        this.topDown = new TopDownView(this.elev, input => this.topDownRoot!.add(input), {multiview: true});
        this.topDown.hideDimensions();

        this.setupLayout(
            FrmSceneLayout.column(
                this.topDown.getLayout(new NavButtons({
                    expand: () => this.elev.navTo("frameTopDown"),
                }))
                    .grow(0.4)
                    .padding(20)
                    .dashed(),
                this.grid.getLayout()
                    .grow(1)
                    .padding(20)
                    .dashed(),
            )
                .grow(1)
                .gap(0)
                .padding(20),
        );
    }

    private scheduleTopDownRebuild() {
        if(this.building) return; // edit emitted mid-rebuild — ignore
        if(this.rebuildScheduled) return;
        this.rebuildScheduled = true;
        // defer so the current emit finishes (grid reconcile runs first) and a burst of
        // edits (e.g. a drag) coalesces to one rebuild per frame.
        requestAnimationFrame(() => {
            this.rebuildScheduled = false;
            if(!this.app || !this.topDownRoot) return; // disposed before the rebuild ran

            // a frozen layout (see DrawFrmApp.layoutFrozen) can't place the new
            // drawables: wait for the drag to end.
            if(this.app.layoutFrozen) {
                this.scheduleTopDownRebuild();
                return;
            }

            this.building = true;
            this.topDown?.destroy();
            this.topDown = null;
            this.topDownRoot.reset();
            this.buildTopDown();
            this.building = false;
        });
    }

    dispose() {
        this.structureSub?.();
        this.structureSub = null;
        this.valueSub?.cancel();
        this.valueSub = null;
        // the top-down view object isn't tracked (its drawables live under
        // topDownRoot, which super.dispose frees) — tear down the view itself here.
        this.topDown?.destroy();
        this.topDown = null;
        super.dispose();
    }
}
